import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Oscar from './Oscar';
import Recorder from './Recorder';
import carolWelcome from './assets/carol-welcome.jpg';
import liquidFlowerBackground from './assets/liquid-flower-bg-v2.jpg';
// Oscar's voice — pre-generated clips for cross-browser certainty (Safari, Chrome, Samsung Internet)
import aWelcome from './assets/audio/oscar-welcome.m4a';
import aRecord from './assets/audio/oscar-record.m4a';
import aThankyou from './assets/audio/oscar-thankyou.m4a';
import './guestbook.css';

/**
 * Video Guestbook — guest "Oscar" recording journey at /guestbook/:eventToken.
 * Standalone, public, mobile-first. Gated by a valid EVENT TOKEN (no admin login,
 * but never open without a token). One screen at a time; ease-of-use first.
 *
 * Motion: Jakub materialize-in (opacity + rise + blur, custom easing) on each screen;
 * Jhey bloom on celebratory beats; prefers-reduced-motion honored in guestbook.css.
 *
 * Build state: welcome → who → relationship → prep are live. record/preview/thankyou
 * are the next block (VGB-3); they render a graceful interim here.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const GUESTBOOK_CHUNK_BYTES = 512 * 1024;   // 512KB slices: retry-cheap and mobile friendly

const CV80BrandHeader = ({ compact = false }) => (
  <header className={`vgb2-brandhead${compact ? ' is-compact' : ''}`}>
    <img className="vgb2-cv80-mark" src="/celebration/cv80-logo.png" alt="CV80" />
    <span className="vgb2-cv80-copy" aria-label="80 Years of Connection">
      <span>80 Years</span>
      <small>of Connection</small>
    </span>
  </header>
);

const MVitationsSignature = ({ compact = false }) => (
  <footer className={`vgb2-mv-signature${compact ? ' is-compact' : ''}`}>
    {!compact && <span className="vgb2-mv-kicker">A video keepsake by</span>}
    <img src="/mvitations-logo.png" alt="M-Vitations" />
    {compact && <span>M-Vitations Video Guestbook</span>}
  </footer>
);

const LiquidFlowerBackdrop = ({ muted = false }) => (
  <div className={`vgb2-liquid-bg${muted ? ' is-muted' : ''}`} aria-hidden="true">
    <img src={liquidFlowerBackground} alt="" draggable="false" />
    <span className="vgb2-liquid-scrim" />
  </div>
);

export default function GuestbookPage() {
  const { eventToken } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [status, setStatus] = useState('loading');   // loading | ready | invalid
  const [step, setStep] = useState('welcome');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [msg, setMsg] = useState(null);               // {message_id, prompts, max_seconds, max_takes}
  const [busy, setBusy] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [startErr, setStartErr] = useState('');
  const [upPct, setUpPct] = useState(0);
  const [upErr, setUpErr] = useState(false);
  const [pendingTake, setPendingTake] = useState(null);   // retry-safe: keep blob for "try again"
  const [voiceOn, setVoiceOn] = useState(false);   // Voice Oscar (opt-in, Web Speech)
  const [gbToken, setGbToken] = useState('');      // personal-link identity (?g=…)
  const [returnIn, setReturnIn] = useState(4);
  const returnPath = useMemo(() => {
    try {
      const value = new URLSearchParams(window.location.search).get('return') || '';
      return value.startsWith('/celebration/') && !value.startsWith('//') ? value : '';
    } catch (e) { return ''; }
  }, []);

  const returnToMain = useCallback(() => {
    if (returnPath) {
      navigate(returnPath);
      return;
    }

    // Direct guestbook links return to a clean welcome screen for the next guest.
    setFirst('');
    setLast('');
    setMsg(null);
    setBusy(false);
    setIdentityOpen(false);
    setStartErr('');
    setUpPct(0);
    setUpErr(false);
    setPendingTake(null);
    setGbToken('');
    setStep('welcome');
    navigate(`/guestbook/${eventToken}`, { replace: true });
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }));
  }, [eventToken, navigate, returnPath]);

  useEffect(() => {
    if (step !== 'finished') {
      setReturnIn(4);
      return undefined;
    }

    const startedAt = Date.now();
    setReturnIn(4);
    const countdown = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setReturnIn(Math.max(0, 4 - elapsed));
    }, 250);
    const redirect = window.setTimeout(returnToMain, 4000);

    return () => {
      window.clearInterval(countdown);
      window.clearTimeout(redirect);
    };
  }, [returnToMain, step]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/guestbook/event/${eventToken}`);
        if (live) { setEvent(r.data); setStatus('ready'); }
      } catch (e) { if (live) setStatus('invalid'); }
    })();
    return () => { live = false; };
  }, [eventToken]);

  // Personal guestbook link (?g=…): resolve → prefill name + carry the guest's identity so the
  // recording links straight to their profile. Invalid token silently falls back to typed name.
  useEffect(() => {
    let live = true;
    let g = '';
    try { g = new URLSearchParams(window.location.search).get('g') || ''; } catch (e) { /* */ }
    if (!g) return undefined;
    setGbToken(g);
    (async () => {
      try {
        const r = await axios.get(`${API}/api/guestbook/g/${encodeURIComponent(g)}`);
        if (live && r.data) {
          if (r.data.first_name) setFirst(r.data.first_name);
          if (r.data.last_name) setLast(r.data.last_name);
        }
      } catch (e) { /* invalid/closed personal link → guest just types their name */ }
    })();
    return () => { live = false; };
  }, []);

  const celebrant = (event && event.celebrant_name) || 'our celebrant';
  const occasion = (event && event.occasion) || '80th Birthday';

  // ── Oscar's voice — pre-generated audio clips (no browser-TTS dependency; works everywhere) ──
  const VOICE_SRC = useMemo(() => ({
    welcome: aWelcome, record: aRecord, thankyou: aThankyou,
  }), []);
  const voiceSupported = typeof Audio !== 'undefined';
  const audioRef = useRef(null);   // points at a real <audio> element rendered below
  const voiceOnRef = useRef(false);
  const voiceSrcRef = useRef('');
  const play = useCallback((src) => {
    try {
      const a = audioRef.current;
      if (!a || !src) return;
      a.pause();
      a.muted = false; a.volume = 1;          // make sure we're audible
      a.setAttribute('src', src);             // always (re)point; bundler gives a real URL
      a.load();
      try { a.currentTime = 0; } catch (e) { /* pre-load seek; ignore */ }
      const p = a.play();
      if (p && p.catch) p.catch(() => { /* autoplay blocked until a gesture; harmless */ });
    } catch (e) { /* voice is a nicety; never break the flow */ }
  }, []);
  const voiceSrc = useMemo(() => {
    switch (step) {
      case 'welcome': return VOICE_SRC.welcome;
      case 'record': return VOICE_SRC.record;
      case 'thankyou': return VOICE_SRC.thankyou;
      default: return '';
    }
  }, [step, VOICE_SRC]);
  // toggle MUST start playback synchronously inside the click to satisfy autoplay policies
  const toggleVoice = useCallback(() => {
    const next = !voiceOnRef.current;
    voiceOnRef.current = next;
    setVoiceOn(next);
    if (next) play(voiceSrcRef.current);
    else { try { audioRef.current && audioRef.current.pause(); } catch (e) { /* */ } }
  }, [play]);
  useEffect(() => {
    voiceSrcRef.current = voiceSrc;
    if (voiceOnRef.current) play(voiceSrc);   // re-play on each new screen while voice is on
  }, [voiceSrc, play]);
  useEffect(() => () => { try { audioRef.current && audioRef.current.pause(); } catch (e) { /* */ } }, []);

  const startMessage = useCallback(async (mode = '') => {
    setBusy(true);
    setStartErr('');
    try {
      const r = await axios.post(`${API}/api/guestbook/event/${eventToken}/messages`,
        { first_name: first.trim(), last_name: last.trim(), relationship: 'Other', mode,
          guestbook_token: gbToken || undefined });
      setMsg(r.data);
      setStep('record');
    } catch (e) {
      setStartErr('We could not open the camera journey. Your information is still here—please try again.');
    }
    setBusy(false);
  }, [eventToken, first, last, gbToken]);

  const silenceOscar = useCallback(() => {
    voiceOnRef.current = false;
    setVoiceOn(false);
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; } } catch (e) { /* */ }
  }, []);

  const beginWelcomeJourney = useCallback(() => {
    if (first.trim() && last.trim()) {
      startMessage('');
      return;
    }
    setIdentityOpen(true);
    setStartErr('');
  }, [first, last, startMessage]);

  // ── resumable, retry-safe chunked upload → finalize ──
  const postChunk = useCallback(async (mid, i, slice, contentType) => {
    const buf = await slice.arrayBuffer();
    let attempt = 0;
    for (;;) {
      try {
        await axios.post(`${API}/api/guestbook/messages/${mid}/chunk`, buf, {
          params: { event_token: eventToken, index: i, content_type: contentType },
          headers: { 'Content-Type': 'application/octet-stream' },
          timeout: 60000,
        });
        return;
      } catch (e) {
        if (++attempt >= 3) throw e;                 // re-PUT same index is idempotent on the server
        await new Promise((r) => setTimeout(r, 600 * attempt));
      }
    }
  }, [eventToken]);
  const uploadAndFinalize = useCallback(async (blob, durationSec, contentType, takesUsed) => {
    if (!msg) return;
    setPendingTake({ blob, durationSec, contentType, takesUsed });
    setUpErr(false); setUpPct(0); setStep('uploading');
    try {
      const total = Math.max(1, Math.ceil(blob.size / GUESTBOOK_CHUNK_BYTES));
      for (let i = 0; i < total; i++) {
        await postChunk(msg.message_id, i, blob.slice(i * GUESTBOOK_CHUNK_BYTES, (i + 1) * GUESTBOOK_CHUNK_BYTES), contentType);
        setUpPct(Math.round(((i + 1) / total) * 100));
      }
      await axios.post(`${API}/api/guestbook/messages/${msg.message_id}/finalize`, {
        event_token: eventToken, content_type: contentType, total_chunks: total,
        duration_sec: durationSec, takes_used: Math.max(1, takesUsed),
      });
      setStep('thankyou');
    } catch (e) { setUpErr(true); }
  }, [msg, eventToken, postChunk]);

  if (status === 'loading') {
    return <div className="vgb vgb2-shell vgb2-is-compact"><LiquidFlowerBackdrop muted /><CV80BrandHeader compact /><div className="vgb-center vgb-anim"><Oscar size={88} /><p className="vgb-sub" style={{ marginTop: 18 }}>Preparing Carol’s video guestbook…</p></div><MVitationsSignature compact /></div>;
  }
  if (status === 'invalid') {
    return (
      <div className="vgb vgb2-shell vgb2-is-compact">
        <LiquidFlowerBackdrop muted />
        <CV80BrandHeader compact />
        <div className="vgb-center vgb-anim">
          <Oscar size={92} />
          <h1 className="vgb-h" style={{ marginTop: 14 }}>This link isn’t active</h1>
          <p className="vgb-sub">This guestbook link is invalid or has been closed. Please check with your host for the correct link.</p>
        </div>
        <MVitationsSignature compact />
      </div>
    );
  }

  // Oscar's voice control — travels with Oscar (under his avatar; inside the record coaching card)
  const VoiceChip = ({ inCard = false }) => voiceSupported ? (
    <button type="button" className={`vgb-voicechip${voiceOn ? ' on' : ''}${inCard ? ' in-card' : ''}`}
      onClick={toggleVoice} aria-pressed={voiceOn}
      aria-label={voiceOn ? 'Turn off Oscar’s voice' : 'Hear Oscar speak'}>
      <span className="vgb-voicechip-ic" aria-hidden="true">{voiceOn ? '🔊' : '🔈'}</span>
      <span>{voiceOn ? 'Oscar’s voice on' : 'Tap to hear Oscar'}</span>
    </button>
  ) : null;

  return (
    <div className={`vgb vgb2-shell vgb2-step-${step}${step !== 'welcome' ? ' vgb2-is-compact' : ''}`}>
      <LiquidFlowerBackdrop muted={step === 'record' || step === 'uploading'} />
      <CV80BrandHeader compact={step !== 'welcome' && step !== 'thankyou' && step !== 'finished'} />
      {voiceSupported && <audio ref={audioRef} preload="auto" playsInline aria-hidden="true" />}

      {/* 1 · WELCOME — Carol is the emotional hero; identity stays inline. */}
      {step === 'welcome' && (
        <main className="vgb2-welcome vgb-scroll" key="welcome">
          <div className="vgb2-carol-portrait" aria-hidden="true">
            <img src={carolWelcome} alt="" draggable="false" />
            <span />
          </div>
          <section className="vgb2-welcome-card vgb-stagger" aria-labelledby="vgb2-welcome-title">
            <div className="vgb2-welcome-copy">
              <span className="vgb2-eyebrow">Carol’s 80th birthday</span>
              <h1 id="vgb2-welcome-title">A message<br />for Carol</h1>
              <p>Share a memory, a wish, or simply say happy birthday.</p>
            </div>

            {gbToken && first && last && !identityOpen && (
              <div className="vgb2-recording-as">
                <span>Recording as</span>
                <strong>{first} {last}</strong>
                <button type="button" onClick={() => setIdentityOpen(true)}>Not you?</button>
              </div>
            )}

            {identityOpen && (
              <div className="vgb2-identity" aria-label="Tell us your name">
                <div className="vgb2-identity-head">
                  <strong>Before we open the camera</strong>
                  <span>Tell Carol who this message is from.</span>
                </div>
                <div className="vgb2-name-grid">
                  <label>
                    <span>First name</span>
                    <input value={first} onChange={(e) => setFirst(e.target.value)} autoComplete="given-name" placeholder="Maria" />
                  </label>
                  <label>
                    <span>Last name</span>
                    <input value={last} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" placeholder="Hernandez" />
                  </label>
                </div>
              </div>
            )}

            {startErr && <p className="vgb2-start-error" role="alert">{startErr}</p>}

            <div className="vgb2-welcome-action">
              <button className="vgb2-primary" disabled={busy || (identityOpen && (!first.trim() || !last.trim()))}
                onClick={identityOpen ? () => startMessage('') : beginWelcomeJourney}>
                <span>{busy ? 'Opening the camera…' : (identityOpen ? 'Continue to camera' : 'Start recording')}</span>
                <span className="vgb2-primary-arrow" aria-hidden="true">→</span>
              </button>
              <p>Private to Carol’s family.</p>
            </div>

            <div className="vgb2-oscar-welcome">
              <Oscar size={104} />
              <div>
                <strong>Oscar will guide you.</strong>
                <span>Your M-Vitations concierge</span>
              </div>
              <VoiceChip />
            </div>
          </section>
        </main>
      )}

      {/* RECORD — welcome moves directly into the live camera workspace. */}
      {step === 'record' && msg && (
        <div className="vgb-stage vgb-scroll" key="record">
          <Recorder
            celebrant={celebrant}
            maxSeconds={msg.max_seconds || 45}
            maxTakes={msg.max_takes || 3}
            prompts={msg.prompts || []}
            voiceChip={<VoiceChip inCard />}
            onRecordingStart={silenceOscar}
            onSubmit={uploadAndFinalize}
          />
        </div>
      )}

      {/* 6 · UPLOADING (calm, never a spinner-of-doom) */}
      {step === 'uploading' && (
        <main className="vgb-stage vgb2-upload-state" key="uploading" aria-live="polite">
          <section className="vgb2-state-card vgb2-upload-card vgb-anim">
            <div className="vgb2-state-orb"><Oscar pose="heart" size={112} /></div>
            {!upErr ? (
              <>
                <span className="vgb2-eyebrow">Private upload</span>
                <h1>Keeping your message safe</h1>
                <p>We’re placing your recording in Carol’s private video keepsake.</p>
                <div className="vgb-upbar" role="progressbar" aria-label="Saving your message" aria-valuemin="0" aria-valuemax="100" aria-valuenow={upPct}><span style={{ width: `${upPct}%` }} /></div>
                <p className="vgb2-upload-meta"><strong>{upPct}%</strong><span>Please keep this page open</span></p>
              </>
            ) : (
              <>
                <span className="vgb2-eyebrow">Recording protected</span>
                <h1>Your message is still here</h1>
                <p>The connection paused before the upload finished. Nothing was lost.</p>
                <div className="vgb2-state-actions">
                  <button className="vgb2-primary" onClick={() => pendingTake && uploadAndFinalize(pendingTake.blob, pendingTake.durationSec, pendingTake.contentType, pendingTake.takesUsed)}>
                    <span>Try sending again</span><span className="vgb2-primary-arrow" aria-hidden="true">↻</span>
                  </button>
                </div>
              </>
            )}
          </section>
        </main>
      )}

      {/* 7 · THANK-YOU */}
      {step === 'thankyou' && (
        <main className="vgb-stage vgb-scroll vgb2-sent-state" key="thankyou">
          <section className="vgb2-state-card vgb2-sent-card vgb-stagger">
            <div className="vgb2-sent-hero" aria-hidden="true">
              <span className="vgb2-envelope-halo" />
              <img src="/mvitations-logo.png" alt="" />
              <Oscar pose="celebrate" size={118} />
            </div>
            <span className="vgb2-eyebrow">Delivered with love</span>
            <h1>Thank you, <span className="vgb2-guest-name">{first || 'friend'}.</span></h1>
            <p>Your message is now part of Carol’s private {occasion} keepsake.</p>
            <div className="vgb2-state-actions vgb2-thankyou-action">
              <button className="vgb2-primary" onClick={() => setStep('finished')}>
                <span>All done</span><span className="vgb2-primary-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </section>
        </main>
      )}

      {/* 8 · FINISHED (closing grace) */}
      {step === 'finished' && (
        <main className="vgb-stage vgb2-finished-state" key="finished">
          <section className="vgb2-state-card vgb2-finished-card vgb-anim">
            <div className="vgb2-finished-oscar"><Oscar pose="wave" size={132} /></div>
            <span className="vgb2-eyebrow">From the heart</span>
            <h1>With love</h1>
            <p>You’re all set. Thank you for being part of {celebrant}’s celebration.</p>
            <span className="vgb2-close-note" aria-live="polite">
              Returning to {returnPath ? 'the celebration' : 'the welcome page'} in {returnIn} seconds.
            </span>
            <div className="vgb2-state-actions vgb2-return-action">
              <button className="vgb2-primary" onClick={returnToMain}>
                <span>{returnPath ? 'Back to the celebration' : 'Return to welcome'}</span>
                <span className="vgb2-primary-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </section>
        </main>
      )}

      <MVitationsSignature compact={step !== 'welcome' && step !== 'thankyou' && step !== 'finished'} />
    </div>
  );
}
